import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { GET_RETURN_EXCHANGE_CUSTOMIZATION_POLICY } from 'gql/queries';
import { UPDATE_RETURN_EXCHANGE_CUSTOMIZATION_POLICY } from 'gql/mutations';
import {
  customizationPolicyKey,
  parseCustomizationConfiguration,
  parseCustomizationPolicy,
  validateCustomizationDraft,
  type CustomizationDraft,
  type CustomizationPolicy,
} from './CustomizationPolicy.contract';

interface State {
  scope: object;
  source: CustomizationPolicy | null;
  draft: CustomizationDraft | null;
  error: string | null;
  saving: boolean;
  saved: boolean;
  needsReload: boolean;
}
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));
const draftOf = (source: CustomizationPolicy): CustomizationDraft =>
  clone({ enabled: source.enabled, configuration: source.configuration });
export default function useCustomizationPolicy(storeId: string | undefined) {
  const owner = useRef({ storeId }),
    mounted = useRef(true),
    pending = useRef(false);
  if (owner.current.storeId !== storeId) {
    owner.current = { storeId };
    pending.current = false;
  }
  const scope = owner.current;
  const empty: State = {
    scope,
    source: null,
    draft: null,
    error: null,
    saving: false,
    saved: false,
    needsReload: false,
  };
  const [state, setState] = useState<State>(empty),
    current = state.scope === scope ? state : empty;
  const latest = useRef(current);
  latest.current = current;
  const read = useQuery(GET_RETURN_EXCHANGE_CUSTOMIZATION_POLICY, {
    variables: { input: { storeId } },
    skip: !storeId,
    fetchPolicy: 'network-only',
  });
  const [write] = useMutation(UPDATE_RETURN_EXCHANGE_CUSTOMIZATION_POLICY);
  const readState = useRef({ loading: read.loading, error: read.error });
  readState.current = { loading: read.loading, error: read.error };
  const raw = read.data?.returnExchangeCustomizationPolicySettings;
  const dirty =
    !!current.source &&
    !!current.draft &&
    customizationPolicyKey(current.source) !==
      customizationPolicyKey(current.draft);
  const live = () => mounted.current && owner.current === scope;
  const update = (next: State) => {
    if (live()) {
      latest.current = next;
      setState(next);
    }
  };
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );
  useEffect(() => {
    const source = storeId ? parseCustomizationPolicy(raw, storeId) : null;
    if (
      source &&
      !read.loading &&
      !read.error &&
      !pending.current &&
      (!current.source ||
        (!dirty &&
          !current.needsReload &&
          source.revision > current.source.revision))
    ) {
      update({ ...empty, source, draft: draftOf(source) });
    }
    // Cached or delayed generations cannot overwrite edits or an acknowledged save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, raw, read.loading, read.error, dirty]);
  function change(draft: CustomizationDraft) {
    if (!live() || pending.current || !latest.current.source) return;
    update({
      ...latest.current,
      draft: clone(draft),
      saved: false,
      error: latest.current.needsReload ? latest.current.error : null,
    });
  }
  async function save(): Promise<boolean> {
    if (
      !live() ||
      pending.current ||
      !storeId ||
      !latest.current.source ||
      !latest.current.draft ||
      latest.current.needsReload ||
      readState.current.loading ||
      readState.current.error
    )
      return false;
    const before = latest.current,
      source = before.source!,
      proposed = clone(before.draft!);
    const classification = proposed.configuration?.originalClassification;
    if (classification) {
      if (Array.isArray(classification.ignoredPropertyKeys))
        classification.ignoredPropertyKeys =
          classification.ignoredPropertyKeys.filter((value) => value !== '');
      if (Array.isArray(classification.prohibitedProperties))
        classification.prohibitedProperties =
          classification.prohibitedProperties.map((property) =>
            property?.match === 'ONE_OF' && Array.isArray(property.values)
              ? {
                  ...property,
                  values: property.values.filter((value) => value !== ''),
                }
              : property,
          );
    }
    if (customizationPolicyKey(source) === customizationPolicyKey(before.draft!))
      return true;
    const invalid = validateCustomizationDraft(proposed);
    if (invalid) {
      update({ ...before, error: invalid, saved: false });
      return false;
    }
    if (source.revision >= 2147483647) {
      update({
        ...before,
        error: 'Customized garment settings reached their revision limit.',
        saved: false,
      });
      return false;
    }
    const draft = {
      enabled: proposed.enabled,
      configuration:
        proposed.configuration === null
          ? null
          : parseCustomizationConfiguration(proposed.configuration),
    };
    pending.current = true;
    update({ ...before, saving: true, saved: false, error: null });
    try {
      const response = await write({
        variables: {
          input: { storeId, expectedRevision: source.revision, ...draft },
        },
      });
      if (!live()) return false;
      const accepted = parseCustomizationPolicy(
        response.data?.updateReturnExchangeCustomizationPolicySettings,
        storeId,
      );
      if (
        response.errors?.length ||
        !accepted ||
        accepted.revision !== source.revision + 1 ||
        customizationPolicyKey(accepted) !== customizationPolicyKey(draft) ||
        (source.revision > 0 &&
          (accepted.createdAt !== source.createdAt ||
            Date.parse(accepted.updatedAt!) < Date.parse(source.updatedAt!)))
      )
        throw new Error('Customization acknowledgement is unverified');
      update({
        scope,
        source: accepted,
        draft: draftOf(accepted),
        error: null,
        saving: false,
        saved: true,
        needsReload: false,
      });
      return true;
    } catch {
      if (live())
        update({
          ...latest.current,
          saving: false,
          saved: false,
          needsReload: true,
          error:
            'Customized garment settings could not be verified. Reload saved settings before trying again.',
        });
      return false;
    } finally {
      if (live()) pending.current = false;
    }
  }
  async function reload() {
    if (!live() || pending.current || !storeId) return;
    pending.current = true;
    update({ ...latest.current, saving: true, saved: false });
    try {
      const response = await read.refetch({ input: { storeId } });
      if (!live()) return;
      const source = parseCustomizationPolicy(
          response.data?.returnExchangeCustomizationPolicySettings,
          storeId,
        ),
        previous = latest.current.source;
      if (
        response.errors?.length ||
        !source ||
        (previous &&
          (source.revision < previous.revision ||
            (source.revision === previous.revision &&
              JSON.stringify(source) !== JSON.stringify(previous)) ||
            (previous.revision > 0 && source.createdAt !== previous.createdAt)))
      )
        throw new Error('Customization read is unverified');
      update({
        scope,
        source,
        draft: draftOf(source),
        error: null,
        saving: false,
        saved: false,
        needsReload: false,
      });
    } catch {
      if (live())
        update({
          ...latest.current,
          saving: false,
          saved: false,
          needsReload: true,
          error:
            'Customized garment settings could not be verified. Reload saved settings before trying again.',
        });
    } finally {
      if (live()) pending.current = false;
    }
  }
  return {
    ...current,
    dirty,
    loading: read.loading,
    unavailable: !current.source,
    readError: read.error,
    change,
    save,
    reload,
  };
}
